import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { describe, type Problem } from "../api/problem";
import { ProblemBox } from "../ui";

/** A forgotten password, reset by a single-use mailed link, while the
 * deployment names no identity provider (ADR-0466 decision 4b; #1977). Two
 * anonymous pages: the ask, opened from the sign-in page, and the page the
 * mailed link opens. */

/** Where the sign-in page's "Forgot your password?" leads. */
export const FORGOT_PASSWORD_PATH = "/auth/forgot-password";

/** Where a mailed reset link lands: `https://<host>/auth/reset-password#<token>`.
 * The token is the **fragment**, which a browser never sends to a server, so it
 * reaches Zenith only in the body this page posts. The server's
 * `notify.PasswordResetPath` is the same address. */
export const RESET_PASSWORD_PATH = "/auth/reset-password";

/** The rule `zenith-api passwd` applies, which the server checks as well. */
export const PASSWORD_MIN_LENGTH = 12;

// Restates ADR-0466 decision 4b, ZENITH.md §6.3 — the ask is answered the same whatever the
// address, so the page cannot say whether a message was sent, only that one is sent where the
// address signs in with a password.
/** What the page says once the ask is sent, whatever the address. */
export const RESET_ASKED = "If that address signs in here with a password, a link to set a new one is on its way. It works once. If nothing arrives, check the address and ask again.";

// Restates ADR-0466 decision 4b, ZENITH.md §6.3 — the one refusal, naming nothing: a spent,
// expired or unknown link, and one for a person whose account has since been disabled, are
// refused alike.
/** The one refusal every unusable link gets. */
export const RESET_REFUSED = "That reset link cannot be used. It may have been used already, or expired. Ask for another from the sign-in page.";

/** The link carried no fragment: somebody cut it short, or opened the page by
 * its address. Said apart because the person can act on it. */
export const RESET_NO_TOKEN = "This link is missing its token. Open the whole link exactly as it arrived in your email.";

// Restates ADR-0466 decision 4b — setting the new password ends every session the person held,
// and starts none: they sign in with it.
/** What the page says once the password is set. */
export const RESET_DONE = "Your new password is set. You have been signed out everywhere you were signed in. Sign in with the new password.";

/** The ask: one address field, one answer. */
export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [asked, setAsked] = useState(false);
  const [problem, setProblem] = useState<Problem>();
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => { first.current?.focus(); }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault(); setBusy(true); setProblem(undefined);
    const { error, response } = await api.POST("/auth/password-reset:request", { body: { email } });
    setBusy(false);
    if (response.ok) { setAsked(true); return; }
    setProblem(describe(error, response.status));
  };

  return (
    <main className="gate">
      <form className="gate-card form" onSubmit={submit}>
        <div>
          <h1>Reset your password</h1>
          <p className="muted">Enter the address you sign in with. We send it a link to set a new password.</p>
        </div>
        {asked
          ? <div role="status"><p>{RESET_ASKED}</p></div>
          : (
            <>
              {problem && <ProblemBox problem={problem} />}
              <div className="field">
                <label htmlFor="reset-email">Email</label>
                <input ref={first} id="reset-email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div className="actions">
                <button type="submit" className="btn btn-primary" disabled={busy}>Send the link</button>
              </div>
            </>
          )}
        <p className="muted"><Link to="/">Back to sign in</Link></p>
      </form>
    </main>
  );
}

/** The fragment, read once, before anything else can see the address. */
function fragment(): string {
  return window.location.hash.startsWith("#") ? window.location.hash.slice(1) : "";
}

/** The page a reset link opens. It takes the token out of the address bar and
 * the history entry as soon as it has read it, so it is not left behind for
 * the next person at this browser, and posts it once, with the new password. */
export function ResetPassword() {
  // Read in the initializer, before the effect below strips it.
  const [token] = useState(fragment);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [refused, setRefused] = useState(false);
  const [problem, setProblem] = useState<Problem>();
  useEffect(() => { window.history.replaceState(window.history.state, "", RESET_PASSWORD_PATH); }, []);

  const mismatch = confirm !== "" && confirm !== password;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password !== confirm || password.length < PASSWORD_MIN_LENGTH) return;
    setBusy(true); setProblem(undefined);
    const { error, response } = await api.POST("/auth/password-reset", { body: { token, password } });
    setBusy(false);
    if (response.ok) { setDone(true); return; }
    if (response.status === 404) { setRefused(true); return; }
    setProblem(describe(error, response.status));
  };

  const card = (body: ReactNode) => (
    <main className="gate">
      <div className="gate-card form">
        <div><h1>Set a new password</h1></div>
        {body}
      </div>
    </main>
  );
  if (!token) return card(<div role="alert" className="problem"><div><strong>{RESET_NO_TOKEN}</strong></div></div>);
  if (refused) return card(<>
    <div role="alert" className="problem"><div><strong>{RESET_REFUSED}</strong></div></div>
    <p className="muted"><Link to={FORGOT_PASSWORD_PATH}>Ask for another link</Link></p>
  </>);
  if (done) return card(<>
    <div role="status"><p>{RESET_DONE}</p></div>
    <p><Link to="/" className="btn btn-primary">Sign in</Link></p>
  </>);
  return (
    <main className="gate">
      <form className="gate-card form" onSubmit={submit}>
        <div>
          <h1>Set a new password</h1>
          <p className="muted">At least {PASSWORD_MIN_LENGTH} characters.</p>
        </div>
        {problem && <ProblemBox problem={problem} />}
        <div className="field">
          <label htmlFor="new-password">New password</label>
          <input id="new-password" type="password" autoComplete="new-password" required minLength={PASSWORD_MIN_LENGTH} value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="confirm-password">Type it again</label>
          <input id="confirm-password" type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} aria-invalid={mismatch || undefined} />
          {mismatch && <p className="muted" role="alert">The two passwords are not the same.</p>}
        </div>
        <div className="actions">
          <button type="submit" className="btn btn-primary" disabled={busy || mismatch || password.length < PASSWORD_MIN_LENGTH}>Set the password</button>
        </div>
      </form>
    </main>
  );
}
